<script lang="ts">
  import { onMount } from 'svelte'
  import EventEditor from './EventEditor.svelte'
  import PersonEditor from './PersonEditor.svelte'
  import TopicEditor from './TopicEditor.svelte'
  import OtherEditor from './OtherEditor.svelte'
  import type { VideoData, Chapter, Person, Event, Topic, Other, HistoricalDate, AnyTypedEntity, User, AnalysisJob, EntityType } from '../lib/types'
  import { toTypedEntities, getFirstMentionTime, sortEntitiesByFirstMention } from '../lib/types'
  import { createWatchTracker } from '../lib/watchTracker'
  import { sendWatchEvents, fetchHeatmap, type HeatmapData } from '../lib/watchApi'
  import type { Conversation } from '../lib/chatApi'
  import VideoInfoModal from './VideoInfoModal.svelte'
  import CrossVideoModal from './CrossVideoModal.svelte'
  import LoginModal from './LoginModal.svelte'
  import GlobalHeader from './GlobalHeader.svelte'
  import TranscriptPanel from './TranscriptPanel.svelte'
  import EntitiesPanel from './EntitiesPanel.svelte'
  import VideoPanel from './VideoPanel.svelte'
  import Users from '../pages/Users.svelte'
  import Admin from '../pages/Admin.svelte'
  import MentionableAdmin from '../pages/MentionableAdmin.svelte'
  import AdminMerge from '../pages/AdminMerge.svelte'
  import Graph from '../pages/Graph.svelte'
  import Channels from '../pages/Channels.svelte'

  type SortMode = 'first-mention' | 'name'
  type EventSortMode = 'first-mention' | 'historical-date' | 'name'
  type PersonSortMode = 'first-mention' | 'name'
  type EntitySortMode = 'first-mention' | 'historical-date' | 'name'
  type TimelineMode = 'video' | 'history'

  function getInitialVideoId(): string | null {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    return params.get('v') || null
  }

  function getInitialStartTime(): number {
    if (typeof window === 'undefined') return 0
    const params = new URLSearchParams(window.location.search)
    const tParam = params.get('t')
    if (!tParam) return 0
    const match = tParam.match(/^(\d+)s?$/)
    return match ? parseInt(match[1], 10) : 0
  }

  let videoId = $state<string | null>(getInitialVideoId())
  let startTime = $state(getInitialStartTime())
  let currentVideoTime = $state(0)
  let videoTitle = $state('')
  let videoData = $state<VideoData | null>(null)
  let isLoadingSnippets = $state(false)
  let isAnalyzing = $state(false)
  let analysisProgress = $state(0)
  let analysisStage = $state<string | null>(null)
  let analysisError = $state<string | null>(null)
  let isTranscribing = $state(false)
  let transcribeProgress = $state(0)
  let transcribeError = $state<string | null>(null)
  let transcribeJobId = $state<string | null>(null)
  let transcribeUnknownCount = $state(0)
  let transcribeStartedAt = $state<number | null>(null)
  let transcribeElapsedSeconds = $state(0)
  const TRANSCRIBE_DURATION_MULTIPLIER = 0.5
  let editingEvent = $state<Event | null>(null)
  let isCreatingEvent = $state(false)
  let editingPerson = $state<Person | null>(null)
  let editingTopic = $state<Topic | null>(null)
  let editingOther = $state<Other | null>(null)
  let entityFilters = $state({ persons: true, events: true, topics: true, others: true })
  let showInferred = $state(true)
  let entitySortMode = $state<EntitySortMode>('first-mention')
  let highlightedPersonId = $state<number | null>(null)
  let highlightedTopicId = $state<number | null>(null)
  let highlightedEventId = $state<number | null>(null)
  let videoPanel: VideoPanel
  let transcriptPanel: TranscriptPanel
  let eventsList: HTMLDivElement

  const HUMAN_DRIVING_TIMEOUT = 3000
  let transcriptHumanDriving = $state(false)
  let transcriptHumanDrivingTimer: ReturnType<typeof setTimeout> | null = null
  let eventsHumanDriving = $state(false)
  let eventsHumanDrivingTimer: ReturnType<typeof setTimeout> | null = null
  let pendingSnippetIndex = $state<number | null>(null)
  let pendingChapterStart = $state<number | null>(null)
  let timelineSortMode = $state<SortMode>('first-mention')
  let eventsSortMode = $state<EventSortMode>('first-mention')
  let personsSortMode = $state<PersonSortMode>('first-mention')
  let videoDuration = $state(0)
  let timelineMode = $state<TimelineMode>('video')
  let expandedPersons = $state<Set<string>>(new Set())

  let currentUser = $state<User | null>(null)
  let showLoginModal = $state(false)
  let devHints = $state<{ email: string; password: string } | null>(null)
  let autoAnalyzeError = $state<string | null>(null)

  let crossVideoModalOpen = $state(false)
  let crossVideoEntityType = $state<EntityType>('topic')
  let crossVideoEntityId = $state<number | null>(null)
  let crossVideoEntityName = $state('')

  let isUsersPage = $derived(window.location.pathname === '/users')
  let isAdminPage = $derived(window.location.pathname === '/admin')
  let adminSubpage = $derived.by(() => {
    const path = window.location.pathname
    if (path === '/admin/entities' || path === '/admin/persons' || path === '/admin/topics') return 'entities'
    if (path === '/admin/merge') return 'merge'
    return null
  })
  let isGraphPage = $derived(window.location.pathname.startsWith('/graph'))
  let isChannelsPage = $derived(window.location.pathname.startsWith('/channels'))
  let graphInitialEntityId = $derived.by(() => {
    if (!isGraphPage) return null
    const param = new URLSearchParams(window.location.search).get('entity_id')
    if (!param) return null
    const parsed = parseInt(param, 10)
    return Number.isNaN(parsed) ? null : parsed
  })

  // Watch heatmap state
  let watchTracker = createWatchTracker()
  let heatmapData = $state<HeatmapData | null>(null)
  let batchSendInterval: ReturnType<typeof setInterval> | null = null
  const BATCH_SEND_INTERVAL = 30_000

  // Analysis job polling
  let analysisPollingInterval: ReturnType<typeof setInterval> | null = null
  const ANALYSIS_POLL_INTERVAL = 2000
  let transcribePollingInterval: ReturnType<typeof setInterval> | null = null
  const TRANSCRIBE_POLL_INTERVAL = 2000

  
  let historyPixelsPerYear = $state(10)
  let videoPixelsPerSecond = $state(0.1)
  let timelineContainer: HTMLDivElement
  let isZoomDragging = $state(false)
  let zoomDragStart = $state<{ x: number, year: number } | null>(null)
  let zoomDragEnd = $state<{ x: number, year: number } | null>(null)
  let videoZoomDragStart = $state<{ x: number, time: number } | null>(null)
  let videoZoomDragEnd = $state<{ x: number, time: number } | null>(null)
  let isScrubbing = $state(false)
  let scrubTime = $state(0)
  let lastScrubTime = $state<number | null>(null)
  let displayTime = $derived.by(() => {
    if (isScrubbing) return scrubTime
    if (lastScrubTime !== null && Math.abs(currentVideoTime - lastScrubTime) > 0.5) {
      return lastScrubTime
    }
    return currentVideoTime
  })

  const timelinePadding = 12
  let historyTimelineWidth = $derived((historyRange.maxYear - historyRange.minYear) * historyPixelsPerYear + 2 * timelinePadding)
  let videoTimelineWidth = $derived(videoDuration * videoPixelsPerSecond + 2 * timelinePadding)

  let fitToViewVideoPixelsPerSecond = $derived.by(() => {
    if (!timelineContainer || videoDuration <= 0) return 0.1
    const availableWidth = timelineContainer.getBoundingClientRect().width - labelColumnWidth - 2 * timelinePadding
    return Math.max(0.1, availableWidth / videoDuration)
  })

  let fitToViewHistoryPixelsPerYear = $derived.by(() => {
    if (!timelineContainer) return 1
    const availableWidth = timelineContainer.getBoundingClientRect().width - labelColumnWidth - 2 * timelinePadding
    const yearSpan = historyRange.maxYear - historyRange.minYear
    if (yearSpan <= 0) return 1
    return Math.max(1, availableWidth / yearSpan)
  })

  let isZoomedIn = $derived.by(() => {
    if (timelineMode === 'video') {
      return videoPixelsPerSecond > fitToViewVideoPixelsPerSecond * 1.1
    } else {
      return historyPixelsPerYear > fitToViewHistoryPixelsPerYear * 1.1
    }
  })

  let labelColumnWidth = $state(150)
  let isResizingLabel = $state(false)
  let showAllHistory = $state(false)
  let timelineTrackWidth = $state(800)
  let timelineHeight = $state(34)
  let isResizingTimeline = $state(false)

  let hasTimelineTracks = $derived(
    (timelineMode === 'video' && sortedTimelineTracks.length > 0) ||
    (timelineMode === 'history' && sortedEventsGroupedByPerson.length > 0)
  )

  let panelWidths = $state({ events: 25, video: 50, transcript: 25 })
  let resizingPanel = $state<'events-video' | 'video-transcript' | null>(null)

  function getFirstMention(entity: Person | Event): number {
    if (!entity.mentions || entity.mentions.length === 0) return Infinity
    return Math.min(...entity.mentions.map(m => m.timecode))
  }

  let sortedPersons = $derived.by(() => {
    const persons = videoData?.persons || []
    if (timelineSortMode === 'name') {
      return [...persons].sort((a, b) => a.name.localeCompare(b.name))
    }
    return [...persons].sort((a, b) => getFirstMention(a) - getFirstMention(b))
  })

  let sortedPersonsPanel = $derived.by(() => {
    const persons = videoData?.persons || []
    if (personsSortMode === 'name') {
      return [...persons].sort((a, b) => a.name.localeCompare(b.name))
    }
    return [...persons].sort((a, b) => getFirstMention(a) - getFirstMention(b))
  })

  let sortedEventsTimeline = $derived.by(() => {
    const allEvents = videoData?.events || []
    const events = allEvents.filter(e => e.mentions.length > 0)
    if (timelineSortMode === 'name') {
      return [...events].sort((a, b) => a.name.localeCompare(b.name))
    }
    return [...events].sort((a, b) => getFirstMention(a) - getFirstMention(b))
  })

  type TimelineTrackItem = { type: 'person', data: Person } | { type: 'event', data: Event }

  let sortedTimelineTracks = $derived.by(() => {
    const persons = (videoData?.persons || []).map(p => ({ type: 'person' as const, data: p }))
    const events = (videoData?.events || []).filter(e => e.mentions.length > 0).map(e => ({ type: 'event' as const, data: e }))
    const combined = [...persons, ...events]
    if (timelineSortMode === 'name') {
      return combined.sort((a, b) => a.data.name.localeCompare(b.data.name))
    }
    return combined.sort((a, b) => getFirstMention(a.data) - getFirstMention(b.data))
  })

  function historicalDateToNumber(date: HistoricalDate | null): number {
    if (!date) return Infinity
    const month = date.month ?? 1
    const day = date.day ?? 1
    return date.year * 10000 + month * 100 + day
  }

  let sortedEvents = $derived.by(() => {
    const allEvents = videoData?.events || []
    const events = allEvents.filter(e => e.mentions.length > 0)
    if (eventsSortMode === 'name') {
      return [...events].sort((a, b) => a.name.localeCompare(b.name))
    }
    if (eventsSortMode === 'historical-date') {
      return [...events].sort((a, b) => historicalDateToNumber(a.dateStart) - historicalDateToNumber(b.dateStart))
    }
    return [...events].sort((a, b) => getFirstMention(a) - getFirstMention(b))
  })

  let filteredEntities = $derived.by(() => {
    if (!videoData) return []
    const allEntities = toTypedEntities(videoData)

    const filtered = allEntities.filter(entity => {
      const typeFilter = (entity.entityType === 'person' && entityFilters.persons) ||
                        (entity.entityType === 'event' && entityFilters.events) ||
                        (entity.entityType === 'topic' && entityFilters.topics) ||
                        (entity.entityType === 'other' && entityFilters.others)
      if (!typeFilter) return false

      const hasMentions = entity.mentions.length > 0
      if (!showInferred && !hasMentions) return false

      return true
    })

    if (entitySortMode === 'name') {
      return filtered.sort((a, b) => a.name.localeCompare(b.name))
    }
    if (entitySortMode === 'historical-date') {
      return filtered.sort((a, b) => {
        const aDate = 'dateStart' in a && a.dateStart ? historicalDateToNumber(a.dateStart) : Infinity
        const bDate = 'dateStart' in b && b.dateStart ? historicalDateToNumber(b.dateStart) : Infinity
        return aDate - bDate
      })
    }
    return sortEntitiesByFirstMention(filtered)
  })

  let entityCounts = $derived.by(() => {
    if (!videoData) return { persons: 0, events: 0, topics: 0, others: 0 }
    const hasMentions = (e: { mentions: unknown[] }) => e.mentions.length > 0
    const personsWithMentions = videoData.persons.filter(hasMentions).length
    const eventsWithMentions = videoData.events.filter(hasMentions).length
    const topicsWithMentions = videoData.topics.filter(hasMentions).length
    const othersWithMentions = (videoData.others || []).filter(hasMentions).length
    return {
      persons: showInferred ? videoData.persons.length : personsWithMentions,
      events: showInferred ? videoData.events.length : eventsWithMentions,
      topics: showInferred ? videoData.topics.length : topicsWithMentions,
      others: showInferred ? (videoData.others || []).length : othersWithMentions,
    }
  })

  function getTopicDurations(topicId: number) {
    return topicsWithDuration.filter(t => t.id === topicId)
  }

  let historyFullRange = $derived.by(() => {
    const events = videoData?.events || []
    const allEventsWithDates = events.filter(e => e.dateStart?.year)
    if (allEventsWithDates.length === 0) return { minYear: 1900, maxYear: 2000 }
    const years = allEventsWithDates
      .flatMap(e => [e.dateStart.year, e.dateEnd?.year].filter((y): y is number => y !== undefined))
    if (years.length === 0) return { minYear: 1900, maxYear: 2000 }
    const minYear = Math.min(...years)
    const maxYear = Math.max(...years)
    const padding = Math.max(10, Math.ceil((maxYear - minYear) * 0.1))
    return { minYear: minYear - padding, maxYear: maxYear + padding }
  })

  let historyRecentRange = $derived.by(() => {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - 100
    return { minYear, maxYear: currentYear + 5 }
  })

  let historyRange = $derived(showAllHistory ? historyFullRange : historyRecentRange)

  let historyEventsWithDates = $derived.by(() => {
    const { minYear, maxYear } = historyRange
    return (videoData?.events || [])
      .filter(e => e.dateStart?.year)
      .filter(e => e.dateStart.year >= minYear && e.dateStart.year <= maxYear)
      .sort((a, b) => historicalDateToNumber(a.dateStart) - historicalDateToNumber(b.dateStart))
  })

  let hasEventsOutsideRange = $derived.by(() => {
    const { minYear, maxYear } = historyRecentRange
    return (videoData?.events || []).some(e =>
      e.dateStart?.year !== undefined && (e.dateStart.year < minYear || e.dateStart.year > maxYear)
    )
  })

  type PersonWithEvents = {
    name: string
    mentionedEvents: Event[]
    personalEvents: Event[]
    earliestYear: number
  }

  let eventsGroupedByPerson = $derived.by(() => {
    const allEvents = historyEventsWithDates

    const personMap = new Map<string, { mentioned: Event[], personal: Event[] }>()
    const eventsWithoutParticipants: Event[] = []

    allEvents.forEach(event => {
      if (event.participants.length === 0) {
        if (event.mentions.length > 0) {
          eventsWithoutParticipants.push(event)
        }
      } else {
        event.participants.forEach(participant => {
          const existing = personMap.get(participant) || { mentioned: [], personal: [] }
          if (event.isPersonalEvent) {
            existing.personal.push(event)
          } else if (event.mentions.length > 0) {
            existing.mentioned.push(event)
          }
          personMap.set(participant, existing)
        })
      }
    })

    const grouped: PersonWithEvents[] = []

    personMap.forEach((personEvents, name) => {
      const allPersonEvents = [...personEvents.mentioned, ...personEvents.personal]
      if (allPersonEvents.length === 0) return

      const sortByDate = (a: Event, b: Event) =>
        historicalDateToNumber(a.dateStart) - historicalDateToNumber(b.dateStart)

      grouped.push({
        name,
        mentionedEvents: personEvents.mentioned.sort(sortByDate),
        personalEvents: personEvents.personal.sort(sortByDate),
        earliestYear: allPersonEvents[0]?.dateStart?.year || Infinity
      })
    })

    grouped.sort((a, b) => a.earliestYear - b.earliestYear)

    if (eventsWithoutParticipants.length > 0) {
      grouped.push({
        name: 'Other events',
        mentionedEvents: eventsWithoutParticipants,
        personalEvents: [],
        earliestYear: eventsWithoutParticipants[0]?.dateStart?.year || Infinity
      })
    }

    return grouped
  })

  let sortedEventsGroupedByPerson = $derived.by(() => {
    const groups = eventsGroupedByPerson
    if (timelineSortMode === 'name') {
      return [...groups].sort((a, b) => a.name.localeCompare(b.name))
    }
    return groups
  })

  function yearToPixels(year: number): number {
    return (year - historyRange.minYear) * historyPixelsPerYear + timelinePadding
  }

  function videoTimeToPercent(time: number): number {
    if (videoDuration === 0) return 0
    return (time / videoDuration) * 100
  }

  function videoTimeToPixels(time: number): number {
    return time * videoPixelsPerSecond + timelinePadding
  }

  function pixelsToVideoTime(pixels: number): number {
    if (videoPixelsPerSecond === 0) return 0
    return (pixels - timelinePadding) / videoPixelsPerSecond
  }

  type AxisTick = { year: number; major: boolean }

  function generateTimeAxisTicks(minYear: number, maxYear: number, trackWidth: number): AxisTick[] {
    const range = maxYear - minYear
    const minPixelsPerLabel = 80
    const minPixelsPerTick = 20
    const targetLabels = Math.max(2, Math.floor(trackWidth / minPixelsPerLabel))
    const rawInterval = range / targetLabels
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)))
    const normalized = rawInterval / magnitude
    const niceMultiplier = normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10
    const majorInterval = Math.max(1, niceMultiplier * magnitude)
    const minorDivisor = niceMultiplier === 1 ? 5 : niceMultiplier === 2 ? 4 : 5
    const minorInterval = majorInterval / minorDivisor
    const pixelsPerMinorTick = (trackWidth / range) * minorInterval
    const effectiveMinorInterval = pixelsPerMinorTick >= minPixelsPerTick ? minorInterval : majorInterval
    const startYear = Math.ceil(minYear / effectiveMinorInterval) * effectiveMinorInterval
    const ticks: AxisTick[] = []
    for (let year = startYear; year <= maxYear; year += effectiveMinorInterval) {
      const isMajor = Math.abs(year % majorInterval) < 0.001
      ticks.push({ year, major: isMajor })
    }
    return ticks
  }

  function generateVideoTimeAxisTicks(startTime: number, endTime: number): number[] {
    const duration = endTime - startTime
    if (duration <= 0) return []
    const minPixelsPerLabel = 80
    const targetLabels = Math.max(2, Math.floor(videoTimelineWidth / minPixelsPerLabel))
    const rawInterval = duration / targetLabels
    let interval: number
    if (rawInterval <= 5) interval = 5
    else if (rawInterval <= 10) interval = 10
    else if (rawInterval <= 30) interval = 30
    else if (rawInterval <= 60) interval = 60
    else if (rawInterval <= 120) interval = 120
    else if (rawInterval <= 300) interval = 300
    else if (rawInterval <= 600) interval = 600
    else interval = 1800
    const ticks: number[] = []
    const firstTick = Math.ceil(startTime / interval) * interval
    for (let time = firstTick; time <= endTime; time += interval) {
      ticks.push(time)
    }
    return ticks
  }



  function handleLabelResizeStart(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    isResizingLabel = true
    window.addEventListener('mousemove', handleLabelResizeMove)
    window.addEventListener('mouseup', handleLabelResizeEnd)
  }

  function handleLabelResizeMove(e: MouseEvent) {
    if (!isResizingLabel) return
    const timelineSection = document.querySelector('.timeline-section')
    if (!timelineSection) return
    const rect = timelineSection.getBoundingClientRect()
    const newWidth = e.clientX - rect.left
    labelColumnWidth = Math.max(100, Math.min(400, newWidth))
  }

  function handleLabelResizeEnd() {
    isResizingLabel = false
    window.removeEventListener('mousemove', handleLabelResizeMove)
    window.removeEventListener('mouseup', handleLabelResizeEnd)
  }

  function handleTimelineResizeStart(e: MouseEvent) {
    e.preventDefault()
    isResizingTimeline = true
    window.addEventListener('mousemove', handleTimelineResizeMove)
    window.addEventListener('mouseup', handleTimelineResizeEnd)
  }

  function handleTimelineResizeMove(e: MouseEvent) {
    if (!isResizingTimeline) return
    const appHeight = window.innerHeight
    const newHeight = ((appHeight - e.clientY) / appHeight) * 100
    timelineHeight = Math.max(5, Math.min(90, newHeight))
  }

  function handleTimelineResizeEnd() {
    isResizingTimeline = false
    window.removeEventListener('mousemove', handleTimelineResizeMove)
    window.removeEventListener('mouseup', handleTimelineResizeEnd)
  }

  function handlePanelResizeStart(panel: 'events-video' | 'video-transcript', e: MouseEvent) {
    e.preventDefault()
    resizingPanel = panel
    window.addEventListener('mousemove', handlePanelResizeMove)
    window.addEventListener('mouseup', handlePanelResizeEnd)
  }

  function handlePanelResizeMove(e: MouseEvent) {
    if (!resizingPanel) return
    const percent = (e.clientX / window.innerWidth) * 100
    if (resizingPanel === 'events-video') {
      const newEventsWidth = Math.max(15, Math.min(50, percent))
      const diff = newEventsWidth - panelWidths.events
      panelWidths = {
        events: newEventsWidth,
        video: Math.max(20, panelWidths.video - diff),
        transcript: panelWidths.transcript
      }
    } else {
      const newTranscriptWidth = Math.max(15, Math.min(50, 100 - percent))
      const diff = newTranscriptWidth - panelWidths.transcript
      panelWidths = {
        events: panelWidths.events,
        video: Math.max(20, panelWidths.video - diff),
        transcript: newTranscriptWidth
      }
    }
  }

  function handlePanelResizeEnd() {
    resizingPanel = null
    window.removeEventListener('mousemove', handlePanelResizeMove)
    window.removeEventListener('mouseup', handlePanelResizeEnd)
  }


  function getPersonalEventIcon(eventName: string): string {
    const name = eventName.toLowerCase()
    if (name.includes('birth') || name.includes('born') || name.includes('geboorte')) return '✱'
    if (name.includes('death') || name.includes('died') || name.includes('overleden')) return '†'
    if (name.includes('marriage') || name.includes('married') || name.includes('huwelijk')) return '⚭'
    return '◆'
  }

  function handleTranscriptScroll() {
    transcriptHumanDriving = true
    if (transcriptHumanDrivingTimer) {
      clearTimeout(transcriptHumanDrivingTimer)
    }
    transcriptHumanDrivingTimer = setTimeout(() => {
      transcriptHumanDriving = false
    }, HUMAN_DRIVING_TIMEOUT)
  }

  function handleEventsScroll() {
    eventsHumanDriving = true
    if (eventsHumanDrivingTimer) {
      clearTimeout(eventsHumanDrivingTimer)
    }
    eventsHumanDrivingTimer = setTimeout(() => {
      eventsHumanDriving = false
    }, HUMAN_DRIVING_TIMEOUT)
  }

  function pixelsToYear(px: number): number {
    return historyRange.minYear + (px - timelinePadding) / historyPixelsPerYear
  }

  function handleTimelineWheel(event: WheelEvent) {
    if (timelineMode !== 'history') return

    const isZoomGesture = event.ctrlKey || event.metaKey

    if (isZoomGesture) {
      event.preventDefault()

      const rect = timelineContainer.getBoundingClientRect()
      const mouseX = event.clientX - rect.left - labelColumnWidth + timelineContainer.scrollLeft
      const yearAtMouse = pixelsToYear(mouseX)

      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
      const newPixelsPerYear = Math.max(1, Math.min(100, historyPixelsPerYear * zoomFactor))

      if (newPixelsPerYear !== historyPixelsPerYear) {
        const oldScrollLeft = timelineContainer.scrollLeft
        const mouseOffsetFromScroll = event.clientX - rect.left - labelColumnWidth

        historyPixelsPerYear = newPixelsPerYear

        requestAnimationFrame(() => {
          const newMouseX = yearToPixels(yearAtMouse)
          timelineContainer.scrollLeft = newMouseX - mouseOffsetFromScroll
        })
      }
    }
  }

  function handleAxisMouseDown(event: MouseEvent) {
    if (event.button !== 0) return

    const rect = timelineContainer.getBoundingClientRect()
    const x = event.clientX - rect.left - labelColumnWidth + timelineContainer.scrollLeft

    isZoomDragging = true

    if (timelineMode === 'video') {
      const time = pixelsToVideoTime(x)
      videoZoomDragStart = { x, time }
      videoZoomDragEnd = { x, time }
    } else {
      const year = pixelsToYear(x)
      zoomDragStart = { x, year }
      zoomDragEnd = { x, year }
    }

    event.preventDefault()
  }

  function handleAxisMouseMove(event: MouseEvent) {
    if (!isZoomDragging) return

    const rect = timelineContainer.getBoundingClientRect()
    const x = event.clientX - rect.left - labelColumnWidth + timelineContainer.scrollLeft

    if (timelineMode === 'video') {
      if (!videoZoomDragStart) return
      const time = pixelsToVideoTime(x)
      videoZoomDragEnd = { x, time }
    } else {
      if (!zoomDragStart) return
      const year = pixelsToYear(x)
      zoomDragEnd = { x, year }
    }
  }

  function handleAxisMouseUp(event: MouseEvent) {
    if (!isZoomDragging) {
      return
    }

    const containerRect = timelineContainer.getBoundingClientRect()
    const availableWidth = containerRect.width - labelColumnWidth

    if (timelineMode === 'video') {
      if (!videoZoomDragStart || !videoZoomDragEnd) {
        isZoomDragging = false
        videoZoomDragStart = null
        videoZoomDragEnd = null
        return
      }

      const startTime = Math.min(videoZoomDragStart.time, videoZoomDragEnd.time)
      const endTime = Math.max(videoZoomDragStart.time, videoZoomDragEnd.time)
      const timeSpan = endTime - startTime

      if (timeSpan < 10) {
        isZoomDragging = false
        videoZoomDragStart = null
        videoZoomDragEnd = null
        return
      }

      const newPixelsPerSecond = Math.max(0.01, Math.min(10, availableWidth / timeSpan))
      videoPixelsPerSecond = newPixelsPerSecond

      requestAnimationFrame(() => {
        timelineContainer.scrollLeft = videoTimeToPixels(startTime)
      })

      isZoomDragging = false
      videoZoomDragStart = null
      videoZoomDragEnd = null
    } else {
      if (!zoomDragStart || !zoomDragEnd) {
        isZoomDragging = false
        zoomDragStart = null
        zoomDragEnd = null
        return
      }

      const startYear = Math.min(zoomDragStart.year, zoomDragEnd.year)
      const endYear = Math.max(zoomDragStart.year, zoomDragEnd.year)
      const yearSpan = endYear - startYear

      if (yearSpan < 1) {
        isZoomDragging = false
        zoomDragStart = null
        zoomDragEnd = null
        return
      }

      const newPixelsPerYear = Math.max(1, Math.min(100, availableWidth / yearSpan))
      historyPixelsPerYear = newPixelsPerYear

      requestAnimationFrame(() => {
        timelineContainer.scrollLeft = yearToPixels(startYear)
      })

      isZoomDragging = false
      zoomDragStart = null
      zoomDragEnd = null
    }
  }

  function handleGlobalMouseMove(event: MouseEvent) {
    if (isZoomDragging) {
      handleAxisMouseMove(event)
    }
    if (isScrubbing) {
      handleScrubberMouseMove(event)
    }
  }

  function handleScrubberMouseDown(event: MouseEvent) {
    if (event.button !== 0 || timelineMode !== 'video') return

    const rect = timelineContainer.getBoundingClientRect()
    const x = event.clientX - rect.left - labelColumnWidth + timelineContainer.scrollLeft
    const time = Math.max(0, Math.min(videoDuration, pixelsToVideoTime(x)))

    scrubTime = time
    isScrubbing = true
    lastScrubTime = time
    videoPanel?.seekTo(time, 2)
    event.preventDefault()
  }

  function handleScrubberMouseMove(event: MouseEvent) {
    if (!isScrubbing || timelineMode !== 'video') return

    const rect = timelineContainer.getBoundingClientRect()
    const x = event.clientX - rect.left - labelColumnWidth + timelineContainer.scrollLeft
    const time = Math.max(0, Math.min(videoDuration, pixelsToVideoTime(x)))

    scrubTime = time
    lastScrubTime = time
    videoPanel?.seekTo(time, 2)
  }

  function handleScrubberMouseUp() {
    isScrubbing = false
  }

  function handleGlobalMouseUp(event: MouseEvent) {
    if (isZoomDragging) {
      handleAxisMouseUp(event)
    }
    if (isScrubbing) {
      handleScrubberMouseUp()
    }
  }

  $effect(() => {
    if (typeof window === 'undefined') return

    window.addEventListener('mouseup', handleGlobalMouseUp)
    window.addEventListener('mousemove', handleGlobalMouseMove)
    window.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp)
      window.removeEventListener('mousemove', handleGlobalMouseMove)
      window.removeEventListener('keydown', handleGlobalKeyDown)
    }
  })

  function handleGlobalKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space') {
      const target = event.target as HTMLElement
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
      if (isInput) return

      event.preventDefault()
      videoPanel?.togglePlay()
    }
  }

  $effect(() => {
    if (lastScrubTime !== null && Math.abs(currentVideoTime - lastScrubTime) <= 0.5) {
      lastScrubTime = null
    }
  })

  function formatHistoricalDate(date: HistoricalDate): string {
    const year = date.year < 0 ? `${Math.abs(date.year)} BC` : `${date.year}`
    if (date.day && date.month) {
      return `${date.day}-${date.month}-${year}`
    }
    if (date.month) {
      return `${date.month}/${year}`
    }
    return year
  }

  function getMostRecentEventIndex(): number {
    if (sortedEvents.length === 0) return -1

    let mostRecentIndex = -1
    let mostRecentTime = -1

    sortedEvents.forEach((event, index) => {
      event.mentions.forEach(mention => {
        if (mention.timecode <= currentVideoTime && mention.timecode > mostRecentTime) {
          mostRecentTime = mention.timecode
          mostRecentIndex = index
        }
      })
    })

    return mostRecentIndex
  }

  let activeEventIndex = $derived(getMostRecentEventIndex())

  type ActiveEntity = { type: 'person' | 'event' | 'topic', id: number, index: number } | null

  let activeEntity = $derived.by(() => {
    if (filteredEntities.length === 0) return null

    let mostRecent: ActiveEntity = null
    let mostRecentTime = -1

    filteredEntities.forEach((entity, index) => {
      entity.mentions.forEach(mention => {
        if (mention.timecode <= currentVideoTime && mention.timecode > mostRecentTime) {
          mostRecentTime = mention.timecode
          mostRecent = { type: entity.entityType, id: entity.id, index }
        }
      })
    })

    return mostRecent
  })

  onMount(async () => {
    parseHashString()
    checkSession()
    await loadFromApi()
    if (!videoId && !isUsersPage && !isAdminPage && !adminSubpage && !isChannelsPage && !isGraphPage) {
      openVideoInfoModal()
    }
  })

  $effect(() => {
    if (!timelineContainer) return
    const observer = new ResizeObserver(entries => {
      timelineTrackWidth = entries[0].contentRect.width - labelColumnWidth
    })
    observer.observe(timelineContainer)
    return () => observer.disconnect()
  })


  $effect(() => {
    timelineMode
    expandedPersons
    updateUrlHash()
  })

  let previousVideoIdForTranscribe: string | null = null
  $effect(() => {
    if (videoId !== previousVideoIdForTranscribe) {
      previousVideoIdForTranscribe = videoId
      stopTranscribePolling()
      isTranscribing = false
      transcribeProgress = 0
      transcribeError = null
      transcribeJobId = null
      transcribeUnknownCount = 0
      transcribeStartedAt = null
      transcribeElapsedSeconds = 0
    }
  })

  $effect(() => {
    if (!isTranscribing || transcribeStartedAt === null) return
    const tick = () => {
      if (transcribeStartedAt !== null) {
        transcribeElapsedSeconds = Math.floor((Date.now() - transcribeStartedAt) / 1000)
      }
    }
    tick()
    const handle = setInterval(tick, 1000)
    return () => clearInterval(handle)
  })

  let transcribeEstimateSeconds = $derived(
    videoDuration > 0 ? Math.round(videoDuration * TRANSCRIBE_DURATION_MULTIPLIER) : 0,
  )

  function toggleHistoryRange() {
    showAllHistory = !showAllHistory
    if (timelineContainer) {
      const availableWidth = timelineContainer.getBoundingClientRect().width - labelColumnWidth
      const newRange = showAllHistory ? historyFullRange : historyRecentRange
      const rangeSpan = newRange.maxYear - newRange.minYear
      historyPixelsPerYear = Math.max(1, availableWidth / rangeSpan)
      requestAnimationFrame(() => {
        timelineContainer.scrollLeft = 0
      })
    }
  }

  function toggleEntityFilter(filter: 'persons' | 'events' | 'topics' | 'others') {
    const allFilters = ['persons', 'events', 'topics', 'others'] as const
    const otherFilters = allFilters.filter(f => f !== filter)
    const isSolo = entityFilters[filter] && otherFilters.every(f => !entityFilters[f])

    if (isSolo) {
      entityFilters = { persons: true, events: true, topics: true, others: true }
    } else {
      entityFilters = { persons: filter === 'persons', events: filter === 'events', topics: filter === 'topics', others: filter === 'others' }
    }
  }

  async function loadFromApi() {
    if (!videoId) return
    try {
      const response = await fetch(`/api/videos/${videoId}`)
      if (response.ok) {
        videoData = await response.json()
        if (startTime > 0) {
          currentVideoTime = startTime
        }
        loadSnippets()
        if (videoData?.analysis_job && (videoData.analysis_job.status === 'pending' || videoData.analysis_job.status === 'processing')) {
          isAnalyzing = true
          analysisProgress = videoData.analysis_job.progress || 0
          analysisStage = videoData.analysis_job.current_stage
          startAnalysisPolling()
        }
      }
    } catch (e) {
      console.log('API not available, starting fresh')
    }
  }

  async function loadSnippets() {
    if (!videoId) return
    isLoadingSnippets = true
    try {
      const response = await fetch(`/api/videos/${videoId}/snippets`)
      if (response.ok) {
        const data = await response.json()
        if (videoData) {
          const indexedSnippets = (data.snippets || []).map((s: any, i: number) => ({ ...s, originalIndex: i }))
          videoData = {
            ...videoData,
            structured_data: {
              ...videoData.structured_data,
              snippets: indexedSnippets,
              chapters: data.chapters,
            }
          }
        }
      }
    } catch (e) {
      console.log('Failed to load snippets', e)
    }
    isLoadingSnippets = false
  }

  function handleTimeUpdate(time: number) {
    currentVideoTime = time
    updateUrlTime(time)

    if (currentUser && videoId) {
      watchTracker.recordTime(time)
    }
  }

  function handleDurationChange(duration: number) {
    videoDuration = duration
    if (duration > 0 && timelineContainer) {
      const availableWidth = timelineContainer.getBoundingClientRect().width - labelColumnWidth
      videoPixelsPerSecond = Math.max(0.1, availableWidth / duration)
    }
  }

  function handleTitleChange(title: string) {
    videoTitle = title
    document.title = title ? `${title} - Outer Screen` : 'Outer Screen'
  }

  function parseYouTubeUrl(input: string): { videoId: string, startTime?: number } | null {
    const trimmed = input.trim()
    if (!trimmed) return null

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ]

    for (const pattern of patterns) {
      const match = trimmed.match(pattern)
      if (match) {
        const result: { videoId: string, startTime?: number } = { videoId: match[1] }
        const urlMatch = trimmed.match(/[?&]t=(\d+)s?/)
        if (urlMatch) {
          result.startTime = parseInt(urlMatch[1], 10)
        }
        return result
      }
    }
    return null
  }

  function resetZoom() {
    if (!timelineContainer) return
    const availableWidth = timelineContainer.getBoundingClientRect().width - labelColumnWidth - 2 * timelinePadding
    if (timelineMode === 'video') {
      if (videoDuration > 0) {
        videoPixelsPerSecond = Math.max(0.1, availableWidth / videoDuration)
        timelineContainer.scrollLeft = 0
      }
    } else {
      const yearSpan = historyRange.maxYear - historyRange.minYear
      if (yearSpan > 0) {
        historyPixelsPerYear = Math.max(1, availableWidth / yearSpan)
        timelineContainer.scrollLeft = 0
      }
    }
  }

  function updateUrlTime(time: number) {
    if (!videoId) return
    const seconds = Math.floor(time)
    const url = new URL(window.location.href)
    url.searchParams.set('v', videoId)
    url.searchParams.set('t', `${seconds}s`)
    const hashString = buildHashString()
    url.hash = hashString
    let urlString = url.toString()
    if (!hashString) {
      urlString = urlString.replace(/#$/, '')
    }
    history.replaceState(null, '', urlString)
  }

  let showVideoInfoModal = $state(false)

  function openVideoInfoModal() {
    showVideoInfoModal = true
  }

  function closeVideoInfoModal() {
    showVideoInfoModal = false
  }

  function loadVideoFromUrl(newVideoId: string, newStartTime?: number) {
    const url = new URL(window.location.href)
    url.searchParams.set('v', newVideoId)
    if (newStartTime) {
      url.searchParams.set('t', `${newStartTime}s`)
    } else {
      url.searchParams.delete('t')
    }
    window.location.href = url.toString()
  }

  function buildHashString(): string {
    const parts: string[] = []
    if (timelineMode === 'history') {
      parts.push('history')
    }
    if (expandedPersons.size > 0) {
      parts.push(`ep=${Array.from(expandedPersons).join(',')}`)
    }
    return parts.join('&')
  }

  function parseHashString() {
    const hash = window.location.hash.slice(1)
    if (!hash) return

    if (hash === 'history' || hash.startsWith('history&')) {
      timelineMode = 'history'
    }

    const params = new URLSearchParams(hash)
    const ep = params.get('ep')
    if (ep) {
      expandedPersons = new Set(ep.split(',').map(decodeURIComponent))
    }
  }

  function updateUrlHash() {
    const url = new URL(window.location.href)
    const hashString = buildHashString()
    url.hash = hashString
    let urlString = url.toString()
    if (!hashString) {
      urlString = urlString.replace(/#$/, '')
    }
    history.replaceState(null, '', urlString)
  }

  function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  function seekToTime(seconds: number) {
    lastScrubTime = seconds
    videoPanel?.seekTo(seconds, 2)
  }

  function seekToSnippet(seconds: number, snippetIndex: number) {
    pendingSnippetIndex = snippetIndex
    pendingChapterStart = null
    seekToTime(seconds)
  }

  function seekToChapter(seconds: number) {
    pendingChapterStart = seconds
    pendingSnippetIndex = null
    seekToTime(seconds)
  }

  $effect(() => {
    if (pendingSnippetIndex !== null) {
      const snippets = videoData?.structured_data?.snippets
      if (snippets) {
        const snippet = snippets[pendingSnippetIndex]
        if (currentVideoTime >= snippet.start && currentVideoTime < snippet.start + snippet.duration) {
          pendingSnippetIndex = null
        }
      }
    }
    if (pendingChapterStart !== null && Math.abs(currentVideoTime - pendingChapterStart) < 1) {
      pendingChapterStart = null
    }
  })

  function getChapterForTime(time: number): Chapter | null {
    const chapters = videoData?.structured_data?.chapters
    if (!chapters?.length) return null

    for (let i = chapters.length - 1; i >= 0; i--) {
      if (time >= chapters[i].start) return chapters[i]
    }
    return null
  }

  let currentChapter = $derived(getChapterForTime(currentVideoTime))

  let chaptersWithDuration = $derived.by(() => {
    const chapters = videoData?.structured_data?.chapters
    if (!chapters?.length || videoDuration <= 0) return []
    return chapters.map((chapter, index) => {
      const nextChapter = chapters[index + 1]
      const end = nextChapter ? nextChapter.start : videoDuration
      return { ...chapter, end, duration: end - chapter.start }
    })
  })

  let topicsWithDuration = $derived.by(() => {
    const topics = videoData?.topics
    if (!topics?.length || videoDuration <= 0) return []

    const chapters = videoData?.structured_data?.chapters || []

    const firstMentionPerTopic = topics
      .map(topic => {
        const nonChapterMentions = topic.mentions.filter(m => m.source !== 'chapter')
        if (nonChapterMentions.length === 0) return null
        const firstMention = nonChapterMentions.reduce((earliest, m) =>
          m.timecode < earliest.timecode ? m : earliest
        )
        return {
          topic,
          timecode: firstMention.timecode,
          source: firstMention.source || 'detected',
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => a.timecode - b.timecode)

    const MIN_TOPIC_PIXELS = 4

    const topicsWithTiming = firstMentionPerTopic.map((item, index) => {
      const nextItem = firstMentionPerTopic[index + 1]
      const nextChapter = chapters.find(c => c.start > item.timecode)
      let end = videoDuration
      if (nextItem && (!nextChapter || nextItem.timecode < nextChapter.start)) {
        end = nextItem.timecode
      } else if (nextChapter) {
        end = nextChapter.start
      }
      return {
        ...item.topic,
        start: item.timecode,
        end,
        duration: end - item.timecode,
        source: item.source,
      }
    })

    const minDurationForZoom = MIN_TOPIC_PIXELS / videoPixelsPerSecond
    const validTopics = topicsWithTiming.filter(t => t.duration >= minDurationForZoom)

    return validTopics.map((topic, index) => {
      const nextTopic = validTopics[index + 1]
      const newEnd = nextTopic ? nextTopic.start : videoDuration
      return {
        ...topic,
        end: newEnd,
        duration: newEnd - topic.start,
      }
    })
  })

  let currentTopic = $derived.by(() => {
    if (!topicsWithDuration.length) return null
    for (let i = topicsWithDuration.length - 1; i >= 0; i--) {
      if (currentVideoTime >= topicsWithDuration[i].start) return topicsWithDuration[i]
    }
    return null
  })

  const HEATMAP_BUCKET_SIZE = 5

  let heatmapBars = $derived.by(() => {
    if (!heatmapData || videoDuration <= 0) return []

    const maxBucket = Math.ceil(videoDuration / HEATMAP_BUCKET_SIZE)
    const maxWatchCount = Math.max(1, ...Object.values(heatmapData))

    const bars: Array<{ bucket: number; start: number; end: number; intensity: number; count: number }> = []

    for (let bucket = 0; bucket < maxBucket; bucket++) {
      const count = heatmapData[String(bucket)] || 0
      if (count > 0) {
        bars.push({
          bucket,
          start: bucket * HEATMAP_BUCKET_SIZE,
          end: Math.min((bucket + 1) * HEATMAP_BUCKET_SIZE, videoDuration),
          intensity: count / maxWatchCount,
          count,
        })
      }
    }

    return bars
  })

  let playheadTopOffset = $derived.by(() => {
    let offset = 28
    if (currentUser && heatmapBars.length > 0) offset += 18
    if (chaptersWithDuration.length > 0) offset += 22
    if (topicsWithDuration.length > 0) offset += 22
    return offset
  })

  function shouldShowChapter(snippetIndex: number): Chapter | null {
    const snippets = videoData?.structured_data?.snippets
    const chapters = videoData?.structured_data?.chapters
    if (!snippets || !chapters?.length) return null

    const currentSnippet = snippets[snippetIndex]
    const currentChapter = getChapterForTime(currentSnippet.start)
    if (!currentChapter) return null

    if (snippetIndex === 0) return currentChapter

    const prevSnippet = snippets[snippetIndex - 1]
    const prevChapter = getChapterForTime(prevSnippet.start)

    if (currentChapter !== prevChapter) return currentChapter
    return null
  }

  let previousActiveSnippetIndex = -1

  $effect(() => {
    const time = currentVideoTime
    const snippets = videoData?.structured_data?.snippets
    const transcriptList = transcriptPanel?.getTranscriptList()
    if (!transcriptList || !snippets || transcriptHumanDriving) return

    const activeIndex = snippets.findIndex(s => time >= s.start && time < s.start + s.duration)
    if (activeIndex === -1 || activeIndex === previousActiveSnippetIndex) return

    const indexDelta = Math.abs(activeIndex - previousActiveSnippetIndex)
    const behavior = previousActiveSnippetIndex === -1 || indexDelta > 3 ? 'instant' : 'smooth'
    previousActiveSnippetIndex = activeIndex

    const activeElement = transcriptList.querySelector(`[data-index="${activeIndex}"]`)
    activeElement?.scrollIntoView({ behavior, block: 'center' })
  })

  function highlightPerson(personId: number) {
    highlightedPersonId = personId
    highlightedEventId = null
    highlightedTopicId = null
    setTimeout(() => {
      highlightedPersonId = null
    }, 5000)
  }

  function highlightEvent(eventId: number) {
    highlightedEventId = eventId
    highlightedPersonId = null
    highlightedTopicId = null
    setTimeout(() => {
      highlightedEventId = null
    }, 5000)
  }

  function highlightTopic(topicId: number) {
    highlightedTopicId = topicId
    highlightedPersonId = null
    highlightedEventId = null
  }

  let previousActiveEntityKey: string | null = null

  $effect(() => {
    const entity = activeEntity
    if (!eventsList || eventsHumanDriving || !entity) return

    const entityKey = `${entity.type}-${entity.id}`
    if (entityKey === previousActiveEntityKey) return

    previousActiveEntityKey = entityKey

    if (entity.type === 'person') {
      highlightPerson(entity.id)
    } else if (entity.type === 'event') {
      highlightEvent(entity.id)
    } else if (entity.type === 'topic') {
      highlightTopic(entity.id)
    }

    const activeElement = eventsList.querySelector(`[data-entity-type="${entity.type}"][data-entity-id="${entity.id}"]`)
    activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })

  async function analyzeVideo() {
    if (!videoId) return
    isAnalyzing = true
    analysisProgress = 0
    analysisStage = null
    analysisError = null
    try {
      const response = await fetch(`/api/videos/${videoId}/analyze`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      })
      if (response.status === 401) {
        showLoginModal = true
        isAnalyzing = false
        return
      }
      if (response.ok) {
        const data = await response.json()
        if (data.analysis_job) {
          analysisProgress = data.analysis_job.progress || 0
          analysisStage = data.analysis_job.current_stage
          startAnalysisPolling()
        } else if (data.youtube_id) {
          videoData = data
          isAnalyzing = false
        }
      }
    } catch (e) {
      console.error('Failed to analyze video', e)
      isAnalyzing = false
    }
  }

  function startAnalysisPolling() {
    if (analysisPollingInterval) return
    analysisPollingInterval = setInterval(pollAnalysisStatus, ANALYSIS_POLL_INTERVAL)
  }

  function stopAnalysisPolling() {
    if (analysisPollingInterval) {
      clearInterval(analysisPollingInterval)
      analysisPollingInterval = null
    }
  }

  async function pollAnalysisStatus() {
    if (!videoId) return
    try {
      const response = await fetch(`/api/videos/${videoId}/analyze_status`)
      if (response.ok) {
        const data = await response.json()
        if (data.analysis_job) {
          const job = data.analysis_job as AnalysisJob
          analysisProgress = job.progress || 0
          analysisStage = job.current_stage
          if (job.status === 'failed') {
            stopAnalysisPolling()
            analysisError = job.error_message || 'Analysis failed'
            await loadFromApi()
            isAnalyzing = false
            analysisProgress = 0
            analysisStage = null
          } else if (job.status === 'completed') {
            stopAnalysisPolling()
            await loadFromApi()
            isAnalyzing = false
            analysisProgress = 0
            analysisStage = null
          }
        } else if (data.youtube_id) {
          stopAnalysisPolling()
          videoData = data
          isAnalyzing = false
          analysisProgress = 0
          analysisStage = null
        }
      }
    } catch (e) {
      console.error('Failed to poll analysis status', e)
    }
  }

  function dismissAnalysisError() {
    stopAnalysisPolling()
    analysisError = null
    isAnalyzing = false
    analysisProgress = 0
    analysisStage = null
  }

  async function transcribeVideo() {
    if (!videoId || isTranscribing) return
    isTranscribing = true
    transcribeProgress = 0
    transcribeError = null
    transcribeJobId = null
    transcribeUnknownCount = 0
    transcribeStartedAt = Date.now()
    transcribeElapsedSeconds = 0
    try {
      const response = await fetch(`/api/videos/${videoId}/transcribe`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      })
      if (response.status === 401) {
        showLoginModal = true
        isTranscribing = false
        transcribeStartedAt = null
        transcribeElapsedSeconds = 0
        return
      }
      if (response.ok) {
        const data = await response.json()
        transcribeJobId = data?.transcribe_job?.job_id || null
        startTranscribePolling()
      } else if (response.status === 409) {
        await loadFromApi()
        isTranscribing = false
        transcribeProgress = 0
        transcribeStartedAt = null
        transcribeElapsedSeconds = 0
      } else {
        const data = await response.json().catch(() => ({}))
        transcribeError = data.error || `Transcribe failed: ${response.status}`
        isTranscribing = false
        transcribeStartedAt = null
        transcribeElapsedSeconds = 0
      }
    } catch (e) {
      console.error('Failed to start transcribe', e)
      transcribeError = 'Network error'
      isTranscribing = false
      transcribeStartedAt = null
      transcribeElapsedSeconds = 0
    }
  }

  const TRANSCRIBE_UNKNOWN_MAX = 5

  function startTranscribePolling() {
    if (transcribePollingInterval) return
    transcribePollingInterval = setInterval(pollTranscribeStatus, TRANSCRIBE_POLL_INTERVAL)
  }

  function stopTranscribePolling() {
    if (transcribePollingInterval) {
      clearInterval(transcribePollingInterval)
      transcribePollingInterval = null
    }
  }

  async function pollTranscribeStatus() {
    if (!videoId) return
    try {
      const url = transcribeJobId
        ? `/api/videos/${videoId}/transcribe_status?job_id=${encodeURIComponent(transcribeJobId)}`
        : `/api/videos/${videoId}/transcribe_status`
      const response = await fetch(url)
      if (!response.ok) {
        transcribeUnknownCount += 1
        if (transcribeUnknownCount >= TRANSCRIBE_UNKNOWN_MAX) {
          stopTranscribePolling()
          transcribeError = `Status check failed (HTTP ${response.status}); try again.`
          isTranscribing = false
          transcribeProgress = 0
          transcribeUnknownCount = 0
          transcribeStartedAt = null
          transcribeElapsedSeconds = 0
        }
        return
      }
      const data = await response.json()
      const job = data.transcribe_job
      if (!job) return
      if (typeof job.progress === 'number') transcribeProgress = job.progress
      if (job.status === 'completed') {
        stopTranscribePolling()
        await loadFromApi()
        isTranscribing = false
        transcribeProgress = 0
        transcribeUnknownCount = 0
        transcribeStartedAt = null
        transcribeElapsedSeconds = 0
      } else if (job.status === 'failed') {
        stopTranscribePolling()
        transcribeError = job.error_message || 'Transcribe failed'
        isTranscribing = false
        transcribeProgress = 0
        transcribeUnknownCount = 0
        transcribeStartedAt = null
        transcribeElapsedSeconds = 0
      } else if (job.status === 'unknown') {
        transcribeUnknownCount += 1
        if (transcribeUnknownCount >= TRANSCRIBE_UNKNOWN_MAX) {
          stopTranscribePolling()
          transcribeError = 'Tank lost track of this job; try again.'
          isTranscribing = false
          transcribeProgress = 0
          transcribeUnknownCount = 0
          transcribeStartedAt = null
          transcribeElapsedSeconds = 0
        }
      } else {
        transcribeUnknownCount = 0
      }
    } catch (e) {
      console.error('Failed to poll transcribe status', e)
    }
  }

  function dismissTranscribeError() {
    stopTranscribePolling()
    transcribeError = null
    isTranscribing = false
    transcribeProgress = 0
    transcribeJobId = null
    transcribeUnknownCount = 0
    transcribeStartedAt = null
    transcribeElapsedSeconds = 0
  }

  async function cancelAnalysis() {
    if (!videoId) return
    try {
      const response = await fetch(`/api/videos/${videoId}/cancel_analysis`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (response.ok) {
        stopAnalysisPolling()
        videoData = await response.json()
        isAnalyzing = false
        analysisProgress = 0
        analysisStage = null
      }
    } catch (e) {
      console.error('Failed to cancel analysis', e)
    }
  }

  let isFetchingDescription = $state(false)

  async function fetchDescription() {
    if (!videoId || isFetchingDescription) return
    isFetchingDescription = true
    try {
      const response = await fetch(`/api/videos/${videoId}/fetch_description`, {
        method: 'POST',
      })
      if (response.ok) {
        const data = await response.json()
        if (videoData) {
          videoData = { ...videoData, description: data.description }
        }
      }
    } catch (e) {
      console.error('Failed to fetch description', e)
    }
    isFetchingDescription = false
  }


  async function toggleAutoAnalyze(enabled: boolean) {
    if (!videoId) return
    autoAnalyzeError = null
    try {
      const response = await fetch(`/api/videos/${videoId}/auto_analyze`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      })
      if (response.status === 401) {
        showLoginModal = true
        return
      }
      if (response.ok && videoData) {
        const data = await response.json()
        videoData = { ...videoData, auto_analyze: data.auto_analyze }
      } else if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        autoAnalyzeError = data.error || `Could not change auto-analyze (${response.status})`
      }
    } catch (e) {
      autoAnalyzeError = 'Network error'
    }
  }

  async function checkSession() {
    try {
      const response = await fetch('/api/session', { credentials: 'same-origin' })
      if (response.ok) {
        const data = await response.json()
        currentUser = data.user
        devHints = data.dev_hints || null
      }
    } catch (e) {
      console.error('Failed to check session', e)
    }
  }

  async function handleLoginSuccess(user: User) {
    currentUser = user
    showLoginModal = false
    await loadFromApi()
  }

  async function logout() {
    try {
      await flushWatchEvents()
      await fetch('/api/session', { method: 'DELETE', credentials: 'same-origin' })
      currentUser = null
      heatmapData = null
    } catch (e) {
      console.error('Failed to logout', e)
    }
  }

  async function loadHeatmap() {
    if (!currentUser || !videoId) {
      heatmapData = null
      return
    }
    heatmapData = await fetchHeatmap(videoId)
  }

  async function flushWatchEvents() {
    if (!currentUser || !videoId) return

    const buckets = watchTracker.getUnsentBuckets()
    if (buckets.length === 0) return

    const success = await sendWatchEvents(videoId, buckets)
    if (success) {
      watchTracker.markBucketsSent(buckets)
      if (heatmapData) {
        buckets.forEach(b => {
          const key = String(b)
          heatmapData![key] = (heatmapData![key] || 0) + 1
        })
        heatmapData = { ...heatmapData }
      }
    }
  }

  function togglePersonExpanded(name: string) {
    const newSet = new Set(expandedPersons)
    if (newSet.has(name)) {
      newSet.delete(name)
    } else {
      newSet.add(name)
    }
    expandedPersons = newSet
  }

  // Heatmap effects
  $effect(() => {
    if (currentUser && videoId) {
      loadHeatmap()

      batchSendInterval = setInterval(flushWatchEvents, BATCH_SEND_INTERVAL)

      return () => {
        flushWatchEvents()
        if (batchSendInterval) {
          clearInterval(batchSendInterval)
          batchSendInterval = null
        }
      }
    }
  })

  $effect(() => {
    videoId
    watchTracker.reset()
  })

  $effect(() => {
    if (typeof window === 'undefined') return

    const handleBeforeUnload = () => flushWatchEvents()
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  function handleEventSaved(savedEvent: Event) {
    if (!videoData) return

    const existingIndex = videoData.events.findIndex(e => e.id === savedEvent.id)
    const newEvents = existingIndex >= 0
      ? videoData.events.map((e, i) => i === existingIndex ? savedEvent : e)
      : [...videoData.events, savedEvent]

    videoData = { ...videoData, events: newEvents }

    editingEvent = null
    isCreatingEvent = false
  }

  function handleEventDeleted(eventId: number) {
    if (!videoData) return

    videoData.events = videoData.events.filter(e => e.id !== eventId)
    videoData = { ...videoData }

    editingEvent = null
  }

  function handleEditorCancel() {
    editingEvent = null
    isCreatingEvent = false
  }

  function handlePersonSaved(savedPerson: Person) {
    if (!videoData) return

    const existingIndex = videoData.persons.findIndex(p => p.id === savedPerson.id)
    const newPersons = existingIndex >= 0
      ? videoData.persons.map((p, i) => i === existingIndex ? savedPerson : p)
      : [...videoData.persons, savedPerson]

    videoData = { ...videoData, persons: newPersons }
    editingPerson = null
  }

  function handlePersonEditorCancel() {
    editingPerson = null
  }

  function handleTopicSaved(savedTopic: Topic) {
    if (!videoData) return

    const existingIndex = videoData.topics.findIndex(t => t.id === savedTopic.id)
    const newTopics = existingIndex >= 0
      ? videoData.topics.map((t, i) => i === existingIndex ? savedTopic : t)
      : [...videoData.topics, savedTopic]

    videoData = { ...videoData, topics: newTopics }
    editingTopic = null
  }

  function handleTopicEditorCancel() {
    editingTopic = null
  }

  function handleOtherSaved(savedOther: Other) {
    if (!videoData) return

    const existingOthers = videoData.others || []
    const existingIndex = existingOthers.findIndex(o => o.id === savedOther.id)
    const newOthers = existingIndex >= 0
      ? existingOthers.map((o, i) => i === existingIndex ? savedOther : o)
      : [...existingOthers, savedOther]

    videoData = { ...videoData, others: newOthers }
    editingOther = null
  }

  function handleOtherEditorCancel() {
    editingOther = null
  }

  function scrollToAndHighlightPersonByName(name: string) {
    const person = videoData?.persons.find(p => p.name === name)
    if (person) scrollToAndHighlightPerson(person.id)
  }

  function scrollToAndHighlightPerson(personId: number) {
    entityFilters.persons = true
    highlightedPersonId = personId
    highlightedEventId = null
    highlightedTopicId = null
    setTimeout(() => {
      const element = eventsList?.querySelector(`[data-entity-type="person"][data-entity-id="${personId}"]`)
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 50)
    setTimeout(() => {
      highlightedPersonId = null
    }, 5000)
  }

  function scrollToAndHighlightEvent(eventId: number) {
    entityFilters.events = true
    highlightedEventId = eventId
    highlightedPersonId = null
    highlightedTopicId = null
    setTimeout(() => {
      const element = eventsList?.querySelector(`[data-entity-type="event"][data-entity-id="${eventId}"]`)
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 50)
    setTimeout(() => {
      highlightedEventId = null
    }, 5000)
  }

</script>

<svelte:head>
  <title>{videoId ? (videoTitle || 'Loading...') : 'Outer Screen'}</title>
</svelte:head>

<VideoInfoModal
  open={showVideoInfoModal}
  {videoId}
  {videoTitle}
  {displayTime}
  onClose={closeVideoInfoModal}
  onLoad={loadVideoFromUrl}
/>

<main class="app">
  <GlobalHeader
    {videoTitle}
    {videoData}
    {currentUser}
    {isAnalyzing}
    {analysisProgress}
    {analysisStage}
    {analysisError}
    {isTranscribing}
    {transcribeProgress}
    {transcribeError}
    {transcribeElapsedSeconds}
    {transcribeEstimateSeconds}
    onOpenVideoInfo={openVideoInfoModal}
    onAnalyze={analyzeVideo}
    onCancelAnalysis={cancelAnalysis}
    onDismissAnalysisError={dismissAnalysisError}
    onTranscribe={transcribeVideo}
    onDismissTranscribeError={dismissTranscribeError}
    onToggleAutoAnalyze={toggleAutoAnalyze}
    {autoAnalyzeError}
    onLogout={logout}
    onShowLogin={() => showLoginModal = true}
  />

  {#if isGraphPage}
    <Graph initialEntityId={graphInitialEntityId} onNavigateHome={() => { window.location.href = '/' }} />
  {:else if adminSubpage === 'entities'}
    <MentionableAdmin />
  {:else if adminSubpage === 'merge'}
    <AdminMerge />
  {:else if isAdminPage}
    <Admin />
  {:else if isChannelsPage}
    <Channels />
  {:else if isUsersPage}
    <Users />
  {:else}
  <div class="top-row" style="height: calc(100% - {timelineHeight}% - 48px)">
    <EntitiesPanel
      entities={filteredEntities}
      {entityFilters}
      {entitySortMode}
      {entityCounts}
      {highlightedPersonId}
      {highlightedEventId}
      {highlightedTopicId}
      {currentUser}
      {videoDuration}
      style="width: calc({panelWidths.events}% - 8px)"
      {getTopicDurations}
      onToggleFilter={toggleEntityFilter}
      onSetSortMode={(mode) => entitySortMode = mode}
      onSeekToTime={seekToTime}
      onEditPerson={(p) => editingPerson = p}
      onEditEvent={(e) => editingEvent = e}
      onEditTopic={(t) => editingTopic = t}
      onEditOther={(o) => editingOther = o}
      onShowCrossVideos={(type, id, name) => {
        crossVideoEntityType = type
        crossVideoEntityId = id
        crossVideoEntityName = name
        crossVideoModalOpen = true
      }}
      onScroll={handleEventsScroll}
    />

    <div class="panel-resize-handle panel-resize-handle-left" onmousedown={(e) => handlePanelResizeStart('events-video', e)}></div>

    <VideoPanel
      bind:this={videoPanel}
      {videoId}
      {startTime}
      {displayTime}
      {currentChapter}
      {currentTopic}
      style="width: calc({panelWidths.video}% - 8px)"
      onTimeUpdate={handleTimeUpdate}
      onDurationChange={handleDurationChange}
      onTitleChange={handleTitleChange}
      onPlay={() => transcriptPanel?.setMode('transcript')}
    />

    <div class="panel-resize-handle panel-resize-handle-right" onmousedown={(e) => handlePanelResizeStart('video-transcript', e)}></div>

    <TranscriptPanel
      bind:this={transcriptPanel}
      {videoId}
      {videoData}
      currentTime={displayTime}
      {currentUser}
      {isLoadingSnippets}
      {isFetchingDescription}
      {pendingSnippetIndex}
      style="width: calc({panelWidths.transcript}% - 8px)"
      onSeekToTime={seekToTime}
      onSeekToSnippet={seekToSnippet}
      onFetchDescription={fetchDescription}
      onTranscriptScroll={handleTranscriptScroll}
      onPlay={() => {}}
    />
  </div>

  <div class="timeline-resize-handle" onmousedown={handleTimelineResizeStart}></div>

  <section class="timeline-section" style="height: {timelineHeight}%">
    <div class="timeline-header">
      <div class="timeline-mode-toggle">
        <button
          class:active={timelineMode === 'video'}
          onclick={() => timelineMode = 'video'}
        >
          Video
        </button>
        <button
          class:active={timelineMode === 'history'}
          onclick={() => timelineMode = 'history'}
        >
          History
        </button>
      </div>
      {#if timelineMode === 'history' && hasEventsOutsideRange}
        <button
          class="history-range-toggle"
          class:active={showAllHistory}
          onclick={toggleHistoryRange}
          title={showAllHistory ? 'Show last 100 years' : 'Show all history'}
        >
          {showAllHistory ? '100y' : 'All'}
        </button>
      {/if}
    </div>

    <div class="timeline-content" bind:this={timelineContainer} onwheel={handleTimelineWheel}>
      <div class="timeline-corner" style="width: {labelColumnWidth}px">
        <div class="sort-compact">
          <button
            class:active={timelineSortMode === 'first-mention'}
            onclick={() => timelineSortMode = 'first-mention'}
            title="Sort by first mention"
          >
            1st<span class="sort-arrow">&#9660;</span>
          </button>
          <button
            class:active={timelineSortMode === 'name'}
            onclick={() => timelineSortMode = 'name'}
            title="Sort by name"
          >
            A-Z<span class="sort-arrow">&#9660;</span>
          </button>
          <button
            class="zoom-reset-btn"
            class:active={isZoomedIn}
            onclick={resetZoom}
            disabled={!isZoomedIn}
            title={isZoomedIn ? 'Fit to view' : 'Zoom in by dragging on the timeline'}
          >
            <span class="zoom-icon">&#128269;</span>
          </button>
        </div>
        <div class="label-resize-handle" onmousedown={handleLabelResizeStart}></div>
      </div>

      <div class="timeline-axis-row">
        {#if timelineMode === 'video'}
          <div
            class="timeline-axis-track timeline-scrubber"
            class:scrubbing={isScrubbing}
            style="width: {videoTimelineWidth}px"
            onmousedown={handleScrubberMouseDown}
          >
            <div class="scrubber-handle" style="left: {videoTimeToPixels(displayTime)}px">
              <svg class="scrubber-arrow" width="16" height="14" viewBox="0 0 16 14">
                <path d="M 3,0 L 13,0 Q 16,0 14,3 L 9.5,11.5 Q 8,14 6.5,11.5 L 2,3 Q 0,0 3,0 Z" fill="#ea333e"/>
                <circle cx="5.5" cy="4" r="0.8" fill="white" fill-opacity="0.6"/>
                <circle cx="10.5" cy="4" r="0.8" fill="white" fill-opacity="0.6"/>
                <circle cx="8" cy="8" r="0.8" fill="white" fill-opacity="0.6"/>
              </svg>
            </div>
            {#each generateVideoTimeAxisTicks(0, videoDuration) as tick}
              <div class="timeline-axis-tick" style="left: {videoTimeToPixels(tick)}px">
                <span class="timeline-axis-tick-label">{formatTime(tick)}</span>
              </div>
            {/each}
          </div>
        {:else}
          <div
            class="timeline-axis-track"
            class:dragging={isZoomDragging}
            style="width: {historyTimelineWidth}px"
            onmousedown={handleAxisMouseDown}
          >
            {#each generateTimeAxisTicks(historyRange.minYear, historyRange.maxYear, historyTimelineWidth) as tick}
              <div
                class="timeline-axis-tick"
                class:minor={!tick.major}
                style="left: {yearToPixels(tick.year)}px"
              >
                {#if tick.major}
                  <span class="timeline-axis-tick-label">{Math.round(tick.year)}</span>
                {/if}
              </div>
            {/each}
            {#if isZoomDragging && zoomDragStart && zoomDragEnd}
              {@const left = Math.min(zoomDragStart.x, zoomDragEnd.x)}
              {@const width = Math.abs(zoomDragEnd.x - zoomDragStart.x)}
              <div class="zoom-selection" style="left: {left}px; width: {width}px"></div>
            {/if}
          </div>
        {/if}
      </div>

      {#if timelineMode === 'video' && videoDuration > 0 && sortedTimelineTracks.length > 0}
        <div class="timeline-playhead-row" style="top: {playheadTopOffset}px">
          <div class="timeline-playhead-spacer" style="width: {labelColumnWidth}px"></div>
          <div class="timeline-playhead-track" style="width: {videoTimelineWidth}px">
            <div class="timeline-playhead" style="left: {videoTimeToPixels(displayTime)}px"></div>
          </div>
        </div>
      {/if}

      {#if timelineMode === 'video' && currentUser && heatmapBars.length > 0}
        <div class="timeline-heatmap-row">
          <div class="timeline-heatmap-label" style="width: {labelColumnWidth}px">Watch History</div>
          <div class="timeline-heatmap-track" style="width: {videoTimelineWidth}px">
            {#each heatmapBars as bar}
              <div
                class="timeline-heatmap-bar"
                class:rewatched={bar.count > 1}
                style="
                  left: {videoTimeToPixels(bar.start)}px;
                  width: {Math.max(2, (bar.end - bar.start) * videoPixelsPerSecond - 1)}px;
                  opacity: {0.3 + bar.intensity * 0.7};
                  height: {8 + bar.intensity * 8}px;
                "
                title="Watched {bar.count}x ({formatTime(bar.start)} - {formatTime(bar.end)})"
              ></div>
            {/each}
          </div>
        </div>
      {/if}

      {#if timelineMode === 'video' && chaptersWithDuration.length > 0}
        <div class="timeline-topics-row timeline-chapters-row" class:has-heatmap={currentUser && heatmapBars.length > 0}>
          <div class="timeline-topics-label" style="width: {labelColumnWidth}px">Chapters</div>
          <div class="timeline-topics-track" style="width: {videoTimelineWidth}px">
            {#each chaptersWithDuration as chapter}
              <button
                class="timeline-topic-block chapter"
                class:active={currentChapter?.start === chapter.start}
                style="left: {videoTimeToPixels(chapter.start)}px; width: {videoTimeToPixels(chapter.duration)}px"
                onmousedown={(e) => e.stopPropagation()}
                onclick={() => seekToChapter(chapter.start)}
                title="{chapter.title} ({formatTime(chapter.start)} - {formatTime(chapter.end)})"
              >
                <span class="timeline-topic-title">{chapter.title}</span>
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if timelineMode === 'video' && topicsWithDuration.length > 0}
        <div class="timeline-topics-row timeline-detected-topics-row" class:has-chapters={chaptersWithDuration.length > 0} class:has-heatmap={currentUser && heatmapBars.length > 0}>
          <div class="timeline-topics-label" style="width: {labelColumnWidth}px">Topics</div>
          <div class="timeline-topics-track" style="width: {videoTimelineWidth}px">
            {#each topicsWithDuration as topic}
              <button
                class="timeline-topic-block detected"
                class:active={currentTopic?.start === topic.start}
                class:advertisement={topic.isAdvertisement}
                style="left: {videoTimeToPixels(topic.start)}px; width: {Math.max(0, topic.duration * videoPixelsPerSecond - 1)}px"
                onmousedown={(e) => e.stopPropagation()}
                onclick={() => seekToTime(topic.start)}
                title="{topic.name} ({formatTime(topic.start)} - {formatTime(topic.end)})"
              >
                <span class="timeline-topic-title">{topic.name}</span>
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if isZoomDragging}
        <div class="timeline-zoom-row">
          <div class="timeline-zoom-spacer" style="width: {labelColumnWidth}px"></div>
          {#if timelineMode === 'video' && videoZoomDragStart && videoZoomDragEnd}
            {@const left = Math.min(videoZoomDragStart.x, videoZoomDragEnd.x)}
            {@const width = Math.abs(videoZoomDragEnd.x - videoZoomDragStart.x)}
            <div class="timeline-zoom-track" style="width: {videoTimelineWidth}px">
              <div class="timeline-zoom-selection" style="left: {left}px; width: {width}px"></div>
            </div>
          {:else if timelineMode === 'history' && zoomDragStart && zoomDragEnd}
            {@const left = Math.min(zoomDragStart.x, zoomDragEnd.x)}
            {@const width = Math.abs(zoomDragEnd.x - zoomDragStart.x)}
            <div class="timeline-zoom-track" style="width: {historyTimelineWidth}px">
              <div class="timeline-zoom-selection" style="left: {left}px; width: {width}px"></div>
            </div>
          {/if}
        </div>
      {/if}

      {#if (timelineMode === 'video' && sortedTimelineTracks.length > 0) || (timelineMode === 'history' && sortedEventsGroupedByPerson.length > 0)}
      <div class="timeline-body" onmousedown={handleAxisMouseDown}>
        {#if timelineMode === 'video'}
          {#each sortedTimelineTracks as track}
            {#if track.type === 'person'}
              <div class="timeline-row">
                <button class="timeline-label timeline-label-clickable timeline-label-person" style="width: {labelColumnWidth}px" title={track.data.description} onclick={() => scrollToAndHighlightPerson(track.data.id)}>
                  {track.data.name}
                </button>
                <div class="timeline-track" style="width: {videoTimelineWidth}px">
                  {#each track.data.mentions as mention}
                    <button
                      class="timeline-marker timeline-marker-person"
                      style="left: {videoTimeToPixels(mention.timecode)}px"
                      onmousedown={(e) => e.stopPropagation()}
                      onclick={() => seekToTime(mention.timecode)}
                      title="{formatTime(mention.timecode)}"
                    ></button>
                  {/each}
                </div>
              </div>
            {:else}
              <div class="timeline-row">
                <button class="timeline-label timeline-label-clickable timeline-label-event" style="width: {labelColumnWidth}px" title={track.data.description} onclick={() => scrollToAndHighlightEvent(track.data.id)}>
                  {track.data.name}
                </button>
                <div class="timeline-track" style="width: {videoTimelineWidth}px">
                  {#each track.data.mentions as mention}
                    <button
                      class="timeline-marker timeline-marker-event"
                      style="left: {videoTimeToPixels(mention.timecode)}px"
                      onmousedown={(e) => e.stopPropagation()}
                      onclick={() => seekToTime(mention.timecode)}
                      title="{formatTime(mention.timecode)}"
                    ></button>
                  {/each}
                </div>
              </div>
            {/if}
          {/each}
        {:else}
          {#each sortedEventsGroupedByPerson as group}
            {@const birthEvent = group.personalEvents.find(e => e.name.toLowerCase().includes('birth') || e.name.toLowerCase().includes('born'))}
            {@const deathEvent = group.personalEvents.find(e => e.name.toLowerCase().includes('death') || e.name.toLowerCase().includes('died'))}
            {@const lifespanStart = birthEvent?.dateStart?.year}
            {@const lifespanEnd = deathEvent?.dateStart?.year}
            <div class="timeline-group">
              <div class="timeline-row timeline-row-header">
                <button class="timeline-label timeline-label-person timeline-label-clickable" style="width: {labelColumnWidth}px" onclick={() => scrollToAndHighlightPersonByName(group.name)}>
                  {group.name}
                </button>
                <div class="timeline-track" style="width: {historyTimelineWidth}px">
                  {#if lifespanStart && lifespanEnd}
                    {@const startPx = yearToPixels(lifespanStart)}
                    {@const endPx = yearToPixels(lifespanEnd)}
                    <div
                      class="timeline-lifespan"
                      style="left: {startPx}px; width: {endPx - startPx}px"
                      title="{group.name}: {lifespanStart} - {lifespanEnd}"
                    ></div>
                  {/if}
                  {#each group.personalEvents as event}
                    {@const px = yearToPixels(event.dateStart.year)}
                    {@const icon = getPersonalEventIcon(event.name)}
                    <button
                      class="timeline-personal-marker"
                      style="left: {px}px"
                      title="{event.name}: {formatHistoricalDate(event.dateStart)}"
                      onclick={() => scrollToAndHighlightEvent(event.id)}
                    >{icon}</button>
                  {/each}
                </div>
              </div>
              {#each group.mentionedEvents as event}
                {@const startPx = yearToPixels(event.dateStart.year)}
                {@const endPx = event.dateEnd?.year ? yearToPixels(event.dateEnd.year) : startPx}
                <div class="timeline-row timeline-row-event">
                  <button class="timeline-label timeline-label-event timeline-label-clickable" style="width: {labelColumnWidth}px" title={event.description} onclick={() => scrollToAndHighlightEvent(event.id)}>
                    {event.name}
                  </button>
                  <div class="timeline-track" style="width: {historyTimelineWidth}px">
                    {#if event.dateEnd?.year && event.dateEnd.year !== event.dateStart.year}
                      <button
                        class="timeline-range"
                        style="left: {startPx}px; width: {endPx - startPx}px"
                        title="{event.dateStart.year} - {event.dateEnd.year}"
                        onclick={() => scrollToAndHighlightEvent(event.id)}
                      ></button>
                    {:else}
                      <button
                        class="timeline-event-marker"
                        style="left: {startPx}px"
                        title="{formatHistoricalDate(event.dateStart)}"
                        onclick={() => scrollToAndHighlightEvent(event.id)}
                      ></button>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {/each}
        {/if}
      </div>
      {/if}
    </div>
  </section>
  {/if}
</main>

{#if editingEvent || isCreatingEvent}
  <EventEditor
    event={editingEvent}
    {videoId}
    onSave={handleEventSaved}
    onDelete={handleEventDeleted}
    onCancel={handleEditorCancel}
  />
{/if}

{#if editingPerson}
  <PersonEditor
    person={editingPerson}
    {videoId}
    onSave={handlePersonSaved}
    onCancel={handlePersonEditorCancel}
  />
{/if}

{#if editingTopic}
  <TopicEditor
    topic={editingTopic}
    {videoId}
    onSave={handleTopicSaved}
    onCancel={handleTopicEditorCancel}
  />
{/if}

{#if editingOther}
  <OtherEditor
    other={editingOther}
    {videoId}
    onSave={handleOtherSaved}
    onCancel={handleOtherEditorCancel}
  />
{/if}

<LoginModal
  open={showLoginModal}
  {devHints}
  onClose={() => showLoginModal = false}
  onLogin={handleLoginSuccess}
/>

<CrossVideoModal
  bind:open={crossVideoModalOpen}
  entityName={crossVideoEntityName}
  entityType={crossVideoEntityType}
  entityId={crossVideoEntityId}
  currentVideoId={videoId}
  {currentUser}
  onClose={() => crossVideoModalOpen = false}
/>

<style>
  .app {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
  }

  .top-row {
    display: flex;
    overflow: hidden;
  }

  .timeline-resize-handle {
    height: 12px;
    background: #1a1a1a;
    border-top: 1px solid #333;
    cursor: ns-resize;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .timeline-resize-handle::before {
    content: '⋯';
    color: #555;
    font-size: 14px;
  }

  .timeline-resize-handle:hover {
    background: #2a2a2a;
  }

  .timeline-resize-handle:hover::before {
    color: #999;
  }

  .panel-resize-handle {
    width: 12px;
    height: 40px;
    background: #1a1a1a;
    border-bottom: 1px solid #333;
    cursor: ew-resize;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .panel-resize-handle-left {
    border-left: 1px solid #333;
  }

  .panel-resize-handle-right {
    border-right: 1px solid #333;
  }

  .panel-resize-handle::before {
    content: '⋮';
    color: #555;
    font-size: 14px;
    letter-spacing: -2px;
  }

  .panel-resize-handle:hover {
    background: #2a2a2a;
  }

  .panel-resize-handle:hover::before {
    color: #999;
  }

  .sort-compact {
    display: flex;
    gap: 0;
  }

  .sort-compact button {
    background: #2a2a2a;
    color: #999;
    border: 1px solid #444;
    padding: 0.15rem 0.4rem;
    cursor: pointer;
    font-size: 0.65rem;
    display: flex;
    align-items: center;
    gap: 2px;
    white-space: nowrap;
    margin-left: -1px;
  }

  .sort-compact button:first-child {
    border-radius: 3px 0 0 3px;
    margin-left: 0;
  }

  .sort-compact button:last-child {
    border-radius: 0 3px 3px 0;
  }

  .sort-compact button:hover:not(:disabled) {
    background: #3a3a3a;
  }

  .sort-compact button.active {
    background: #3a5a8a;
    border-color: #4a9eff;
    color: #aaccff;
    z-index: 1;
    position: relative;
  }

  .sort-compact .sort-arrow {
    font-size: 0.45rem;
    margin-left: 2px;
    opacity: 0.7;
  }

  .zoom-reset-btn {
    padding: 0.15rem 0.25rem !important;
  }

  .zoom-reset-btn:disabled {
    color: #555 !important;
    cursor: default !important;
  }

  .zoom-reset-btn .zoom-icon {
    font-size: 0.55rem;
  }

  .timeline-section {
    background: #0d0d0d;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .timeline-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #333;
    flex-shrink: 0;
  }

  .timeline-mode-toggle {
    display: flex;
    gap: 0;
  }

  .timeline-mode-toggle button {
    background: #2a2a2a;
    color: #999;
    border: 1px solid #444;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    font-size: 0.75rem;
    margin-left: -1px;
  }

  .timeline-mode-toggle button:first-child {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }

  .timeline-mode-toggle button:last-child {
    border-radius: 0 4px 4px 0;
  }

  .timeline-mode-toggle button:hover:not(:disabled) {
    background: #3a3a3a;
  }

  .timeline-mode-toggle button.active {
    background: #3a5a8a;
    border-color: #4a9eff;
    color: #aaccff;
    z-index: 1;
    position: relative;
  }

  .history-range-toggle {
    background: #2a2a2a;
    color: #999;
    border: 1px solid #444;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .history-range-toggle:hover {
    background: #3a3a3a;
  }

  .history-range-toggle.active {
    background: #9a6aef;
    color: #fff;
    border-color: #9a6aef;
  }

  .timeline-current-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    justify-content: center;
    min-width: 0;
  }

  .timeline-current-time {
    color: #fff;
    font-family: monospace;
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  .timeline-current-chapter {
    color: #aaa;
    font-size: 0.85rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timeline-content {
    flex: 1;
    min-height: 0;
    position: relative;
    overflow: auto;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: 28px min-content min-content min-content min-content min-content;
    align-content: start;
  }

  .timeline-corner {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 20;
    background: #0d0d0d;
    border-right: 1px solid #333;
    border-bottom: 1px solid #333;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.25rem;
    gap: 0.15rem;
  }

  .label-resize-handle {
    position: absolute;
    right: -1px;
    top: 0;
    bottom: 0;
    width: 12px;
    cursor: col-resize;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label-resize-handle::before {
    content: '⋮';
    color: #555;
    font-size: 14px;
  }

  .label-resize-handle:hover {
    background: #2a2a2a;
  }

  .label-resize-handle:hover::before {
    color: #999;
  }

  .timeline-axis-row {
    grid-column: 2;
    grid-row: 1;
    position: sticky;
    top: 0;
    z-index: 15;
    height: 28px;
    background: #111;
    border-bottom: 1px solid #333;
    display: flex;
    width: max-content;
    min-width: 100%;
  }

  .timeline-axis-label {
    flex-shrink: 0;
    position: sticky;
    left: 0;
    background: #111;
    z-index: 16;
    border-right: 1px solid #333;
  }

  .timeline-axis-track {
    position: relative;
    height: 100%;
    cursor: crosshair;
    user-select: none;
  }

  .timeline-axis-track.dragging {
    cursor: col-resize;
  }

  .timeline-scrubber {
    cursor: pointer;
  }

  .timeline-scrubber:hover,
  .timeline-scrubber.scrubbing {
    cursor: ew-resize;
  }

  .scrubber-handle {
    position: absolute;
    bottom: -7px;
    transform: translateX(-50%);
    z-index: 100;
    pointer-events: none;
  }

  .scrubber-arrow {
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }

  .timeline-axis-tick {
    position: absolute;
    top: 0;
    bottom: 0;
    border-left: 1px solid #444;
    pointer-events: none;
  }

  .timeline-axis-tick.minor {
    border-left-color: #2a2a2a;
    top: 60%;
  }

  .timeline-axis-tick-label {
    position: absolute;
    top: 4px;
    left: 4px;
    color: #666;
    font-size: 0.65rem;
    font-family: monospace;
    white-space: nowrap;
  }

  .zoom-selection {
    position: absolute;
    top: 0;
    bottom: 0;
    background: rgba(74, 158, 255, 0.3);
    border-left: 2px solid #4a9eff;
    border-right: 2px solid #4a9eff;
    pointer-events: none;
  }

  .timeline-playhead-row {
    position: sticky;
    height: 0;
    display: flex;
    pointer-events: none;
    z-index: 10;
    grid-column: 1 / -1;
    overflow: visible;
  }

  .timeline-playhead-spacer {
    flex-shrink: 0;
    position: sticky;
    left: 0;
  }

  .timeline-playhead-track {
    position: relative;
    overflow: visible;
  }

  .timeline-playhead-row .timeline-playhead {
    position: absolute;
    top: 0;
    height: 100vh;
    width: 2px;
    background: #ea333e;
  }

  .timeline-heatmap-row {
    display: flex;
    height: 18px;
    background: #1a1a1a;
    border-bottom: 1px solid #333;
    grid-column: 1 / -1;
    position: sticky;
    top: 28px;
    z-index: 11;
  }

  .timeline-heatmap-label {
    flex-shrink: 0;
    position: sticky;
    left: 0;
    z-index: 5;
    background: #1a1a1a;
    font-size: 0.7rem;
    color: #555;
    display: flex;
    align-items: center;
    padding-left: 0.5rem;
  }

  .timeline-heatmap-track {
    position: relative;
    display: flex;
    align-items: flex-end;
  }

  .timeline-heatmap-bar {
    position: absolute;
    bottom: 2px;
    background: #4a9eff;
    border-radius: 1px;
    min-height: 4px;
    transition: opacity 0.15s ease;
  }

  .timeline-heatmap-bar.rewatched {
    background: #7ab8ff;
  }

  .timeline-heatmap-bar:hover {
    opacity: 1 !important;
    background: #6ab0ff;
  }

  .timeline-topics-row {
    display: flex;
    height: 22px;
    background: #1a1a1a;
    border-bottom: 1px solid #333;
    grid-column: 1 / -1;
    position: sticky;
    z-index: 12;
  }

  .timeline-chapters-row {
    top: 28px;
  }

  .timeline-chapters-row.has-heatmap {
    top: 46px;
  }

  .timeline-detected-topics-row {
    top: 28px;
  }

  .timeline-detected-topics-row.has-heatmap {
    top: 46px;
  }

  .timeline-detected-topics-row.has-chapters {
    top: 50px;
  }

  .timeline-detected-topics-row.has-chapters.has-heatmap {
    top: 68px;
  }

  .timeline-topics-label {
    flex-shrink: 0;
    position: sticky;
    left: 0;
    z-index: 5;
    background: #1a1a1a;
    font-size: 0.7rem;
    color: #666;
    display: flex;
    align-items: center;
    padding-left: 0.5rem;
  }

  .timeline-topics-track {
    position: relative;
    display: flex;
  }

  .timeline-topic-block {
    position: absolute;
    top: 2px;
    height: 18px;
    background: #2a2a2a;
    border: 1px solid #444;
    border-radius: 3px;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: center;
    padding: 0 4px;
    box-sizing: border-box;
  }

  .timeline-topic-block.chapter {
    background: #2a2a2a;
    border-style: solid;
  }

  .timeline-topic-block.detected {
    background: #1a2a1a;
    border-style: solid;
    border-color: #3a5a3a;
  }

  .timeline-topic-block.advertisement {
    background: #2a1a2a;
    border-color: #6a2a6a;
  }

  .timeline-topic-block:hover {
    background: #3a3a3a;
    border-color: #555;
  }

  .timeline-topic-block.detected:hover {
    background: #2a3a2a;
    border-color: #4a6a4a;
  }

  .timeline-topic-block.advertisement:hover {
    background: #3a2a3a;
    border-color: #8a3a8a;
  }

  .timeline-topic-block.active {
    background: #2a4a6a;
    border-color: #4a9eff;
  }

  .timeline-topic-block.detected.active {
    background: #2a4a3a;
    border-color: #4aef7a;
  }

  .timeline-topic-block.advertisement.active {
    background: #4a2a4a;
    border-color: #cf4aef;
  }

  .timeline-topic-title {
    font-size: 0.6rem;
    color: #aaa;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .timeline-topic-block.active .timeline-topic-title {
    color: #fff;
  }

  .timeline-zoom-row {
    position: absolute;
    top: 28px;
    bottom: 0;
    display: flex;
    pointer-events: none;
    z-index: 5;
  }

  .timeline-zoom-spacer {
    flex-shrink: 0;
    position: sticky;
    left: 0;
  }

  .timeline-zoom-track {
    position: relative;
  }

  .timeline-zoom-selection {
    position: absolute;
    top: 0;
    bottom: 0;
    background: rgba(74, 158, 255, 0.2);
    border-left: 2px solid #4a9eff;
    border-right: 2px solid #4a9eff;
  }

  .timeline-body {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: flex-start;
    width: max-content;
    min-width: 100%;
    cursor: crosshair;
  }

  .timeline-row {
    display: flex;
    height: 28px;
    border-bottom: 1px solid #222;
    width: max-content;
    min-width: 100%;
  }

  .timeline-group {
    border-bottom: 2px solid #333;
  }

  .timeline-group:last-child {
    border-bottom: none;
  }

  .timeline-row-header {
    background: #1a1a1a;
  }

  .timeline-row-header .timeline-track {
    background: #1a1a1a;
  }

  .timeline-row-event {
    background: #0d0d0d;
  }

  .timeline-label {
    flex-shrink: 0;
    padding-left: 0.5rem;
    display: flex;
    align-items: center;
    color: #aaa;
    font-size: 0.7rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: #0d0d0d;
    position: sticky;
    left: 0;
    z-index: 5;
    border-right: 1px solid #333;
    border-top: none;
    border-bottom: none;
    border-left: none;
  }

  .timeline-label-clickable {
    cursor: pointer;
    text-align: left;
  }

  .timeline-label-person {
    border-left: 3px solid #8a6ab0;
    color: #b8a;
  }

  .timeline-label-person:hover {
    background: #3a2a4a;
    color: #fff;
  }

  .timeline-label-event {
    border-left: 3px solid #a08040;
    color: #da6;
  }

  .timeline-label-event:hover {
    background: #3a3528;
    color: #fff;
  }

  .timeline-label-topic {
    border-left: 3px solid #4a8a5a;
    color: #8c8;
  }

  .timeline-label-topic:hover {
    background: #2a3a2f;
    color: #fff;
  }

  .timeline-label-other {
    border-left: 3px solid #8a5a40;
    color: #c96;
  }

  .timeline-label-other:hover {
    background: #3a2f2a;
    color: #fff;
  }

  .timeline-track {
    flex: 0 0 auto;
    position: relative;
    overflow: hidden;
  }

  .timeline-playhead-container {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 10;
  }

  .timeline-playhead {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #ea333e;
  }

  .timeline-marker {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    padding: 0;
  }

  .timeline-marker-person {
    background: #5a8ac0;
  }

  .timeline-marker-person:hover {
    background: #7aaae0;
    transform: translate(-50%, -50%) scale(1.5);
  }

  .timeline-marker-event {
    background: #c09050;
  }

  .timeline-marker-event:hover {
    background: #d0a060;
    transform: translate(-50%, -50%) scale(1.5);
  }

  .timeline-marker-event.website {
    background: #50a0c0;
  }

  .timeline-marker-event.website:hover {
    background: #70c0e0;
    transform: translate(-50%, -50%) scale(1.5);
  }

  .timeline-event-marker {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    background: #4a9eff;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    padding: 0;
  }

  .timeline-event-marker:hover {
    background: #6ab0ff;
    transform: translate(-50%, -50%) scale(1.3);
  }

  .timeline-range {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 8px;
    background: linear-gradient(90deg, #4a9eff 0%, #6ab0ff 100%);
    border-radius: 4px;
    min-width: 4px;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .timeline-range:hover {
    background: linear-gradient(90deg, #6ab0ff 0%, #8ac0ff 100%);
    height: 10px;
  }

  .timeline-lifespan {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 2px;
    background: linear-gradient(90deg, rgba(154, 106, 239, 0.4) 0%, rgba(154, 106, 239, 0.2) 50%, rgba(154, 106, 239, 0.4) 100%);
    min-width: 4px;
  }

  .timeline-personal-marker {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.7rem;
    color: #9a6aef;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    padding: 0.25rem;
  }

  .timeline-personal-marker:hover {
    color: #b88aff;
    transform: translate(-50%, -50%) scale(1.3);
  }

  .user-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .user-indicator .user-name {
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-indicator .logout-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.7rem;
    background: transparent;
    border: 1px solid #4b5563;
    color: #9ca3af;
    border-radius: 4px;
    cursor: pointer;
  }

  .user-indicator .logout-btn:hover {
    background: #374151;
    color: #fff;
  }
</style>
